import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
import { apiBaseUrl, baseUrl } from 'App';
import { ApiDocsData, ApiEndpoint, ApiParam, ConsoleData, FieldProperty } from './DocsTypes';
import { getCsrfToken } from 'services/authentication/csrfFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import { faBars, faXmark } from '@fortawesome/free-solid-svg-icons';
import logoBlack from 'assets/crafter-logo-black.png';
import '../../style/scss/default-elements.scss';
import '../../style/scss/button.scss';
import '../../style/scss/docs.scss';

// Base path of the api docs page used for deep links
const DOCS_BASE_PATH = '/api-docs/v3';

// Converts a section or endpoint name into a url slug
const slugify = (name: string): string => {
    return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

const ApiDocs: React.FC = () => {
    const [data, setData] = useState<ApiDocsData | null>(null);
    const [selectedSection, setSelectedSection] = useState<{ type: 'intro' | 'group' | 'subgroup'; name: string } | null>(null);
    const [openGroup, setOpenGroup] = useState<string | null>(null);
    const [activeEndpoint, setActiveEndpoint] = useState<ApiEndpoint | null>(null);
    const introRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const subgroupRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [tryMode, setTryMode] = useState(false);
    const [consoleData, setConsoleData] = useState<ConsoleData>({ inputs: {}, headers: {}, body: "", response: null });
    const [isLoading, setIsLoading] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const restApiUrl = `${baseUrl}/api/rest/v3/`;
    const history = useHistory();
    const location = useLocation();

    // Fetch the api documentation
    useEffect(() => {
        const fetchApiEndpoints = async () => {
            try {
                const response = await axios({
                    url: `${apiBaseUrl}/get_rest_api_documentation/`,
                    method: 'get',
                    headers: { 'X-CSRFToken': getCsrfToken() }
                })

                if (response.status === 200) {
                    setData(response.data);
                }
            } catch (error) {
                if (axios.isCancel(error)) {
                    console.log('Request canceled', error.message);
                } else {
                    console.log('Fetching data not possible', error);
                }
            }
        }

        // Execute the fetch
        fetchApiEndpoints();
    }, []);

    // Reset the console data when the endpoint changes
    useEffect(() => {
        if (activeEndpoint) {
            // Get the previous authorization token, if it exists
            const prevToken = consoleData.headers?.authorization;

            // Prepare the new headers, keeping the previous token if present
            const newHeaders = {
                ...activeEndpoint.request_example?.default_headers,
                ...(prevToken && { authorization: prevToken }),
            };

            // Reset console data, preserving the token
            setConsoleData({
                inputs: {},
                headers: newHeaders,
                body: "",
                response: null,
            });
        }
    }, [activeEndpoint]);

    // Handle scroll to section
    const handleScrollToSection = (type: 'intro' | 'group' | 'subgroup', name: string, parentGroupName?: string) => {
        setSelectedSection({ type, name });

        if (type === 'group') {
            setOpenGroup(prev => (prev === name ? null : name));
        }

        if (type === 'subgroup' && parentGroupName) {
            setOpenGroup(parentGroupName);
        }

        // Put the selected section in the url so it can be shared
        if (type === 'intro') history.replace(`${DOCS_BASE_PATH}/intro/${slugify(name)}`);
        if (type === 'group') history.replace(`${DOCS_BASE_PATH}/${slugify(name)}`);
        if (type === 'subgroup' && parentGroupName) history.replace(`${DOCS_BASE_PATH}/${slugify(parentGroupName)}/${slugify(name)}`);

        let element: HTMLDivElement | null = null;
        if (type === 'intro') element = introRefs.current[name] || null;
        if (type === 'group') element = groupRefs.current[name] || null;
        if (type === 'subgroup') element = subgroupRefs.current[name] || null;

        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Open or close an endpoint and put it in the url so it can be shared
    const handleToggleEndpoint = (endpoint: ApiEndpoint, groupName: string, subgroupName?: string | null) => {

        // Close the endpoint when it is already active
        if (activeEndpoint?.endpoint_name === endpoint.endpoint_name) {
            setActiveEndpoint(null);
            history.replace(subgroupName
                ? `${DOCS_BASE_PATH}/${slugify(groupName)}/${slugify(subgroupName)}`
                : `${DOCS_BASE_PATH}/${slugify(groupName)}`);
            return;
        }

        // Open the endpoint and push its deep link
        setActiveEndpoint(endpoint);
        history.push(`${DOCS_BASE_PATH}/${slugify(groupName)}/${slugify(subgroupName || 'endpoint')}/${slugify(endpoint.endpoint_name)}`);
    };

    // Open the section or endpoint from the url when the data is loaded
    useEffect(() => {
        if (!data) return;

        // Get the path segments after the docs base path
        const segments = location.pathname
            .replace(DOCS_BASE_PATH, '')
            .split('/')
            .filter(segment => segment !== '');
        if (segments.length === 0) return;

        // Open an introduction section from the url
        if (segments[0] === 'intro' && segments[1]) {
            const section = data.introduction.find(entry => slugify(entry.name) === segments[1]);
            if (section) {
                setSelectedSection({ type: 'intro', name: section.name });
                setTimeout(() => introRefs.current[section.name]?.scrollIntoView({ block: 'start' }), 0);
            }
            return;
        }

        // Find the group from the url
        const group = data.reference.find(entry => slugify(entry.name) === segments[0]);
        if (!group) return;
        setOpenGroup(group.name);

        // Open the group from the url
        if (segments.length === 1) {
            setSelectedSection({ type: 'group', name: group.name });
            setTimeout(() => groupRefs.current[group.name]?.scrollIntoView({ block: 'start' }), 0);
            return;
        }

        // Find the subgroup from the url
        const subgroup = group.subgroups.find(entry => entry.name && slugify(entry.name) === segments[1]);

        // Open the subgroup from the url
        if (segments.length === 2 && subgroup?.name) {
            setSelectedSection({ type: 'subgroup', name: subgroup.name });
            setTimeout(() => subgroupRefs.current[subgroup.name!]?.scrollIntoView({ block: 'start' }), 0);
            return;
        }

        // Open the endpoint from the url
        if (segments.length === 3) {
            const endpoints = subgroup ? subgroup.endpoints : group.endpoints;
            const endpoint = endpoints.find(entry => slugify(entry.endpoint_name) === segments[2]);
            if (endpoint) {
                setSelectedSection(subgroup?.name
                    ? { type: 'subgroup', name: subgroup.name }
                    : { type: 'group', name: group.name });
                setActiveEndpoint(endpoint);
                const target = subgroup?.name ? subgroupRefs.current[subgroup.name] : groupRefs.current[group.name];
                setTimeout(() => target?.scrollIntoView({ block: 'start' }), 0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // Render json example
    const renderJsonWithHighlight = (data: Record<string, any>) => {
        const jsonString = JSON.stringify(data, null, 2);
        const lines = jsonString.split("\n");

        const highlightLine = (line: string) => {
            // Escape the html
            let escaped = line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

            // Colons after keys
            escaped = escaped.replace(/"(.*?)":/g, `<span class="json-key">"$1"</span>:`);

            // String values between quotes
            escaped = escaped.replace(/: "(.*?)"/g, `: <span class="json-string">"$1"</span>`);

            // Replace braces
            escaped = escaped.replace(/([{}\[\]])/g, `<span class="json-brace">$1</span>`);

            return escaped;
        };

        return (
            <pre className="json-viewer">
                {lines.map((line, index) => (
                    <div className="json-line" key={index}>
                        <span className="json-linenumber">
                            {(index + 1).toString().padStart(2, "0")}
                        </span>{" "}
                        <span
                            className="json-content"
                            dangerouslySetInnerHTML={{ __html: highlightLine(line) }}
                        />
                    </div>
                ))}
            </pre>
        );
    };

    // Handle console change
    const handleConsoleChange = (field: "inputs" | "headers" | "body", nameOrValue: string, value?: string) => {
        setConsoleData(prev => 
            field === "body" 
            ? { ...prev, body: nameOrValue }
            : { ...prev, [field]: { ...prev[field], [nameOrValue]: value } }
        );
    };

    // Generate a preview url with the entered path and query params
    const generatePreviewUrl = (endpoint: ApiEndpoint, values: Record<string, string>): string => {
        if (!endpoint) return "";

        // Path params
        let newUrl = endpoint.url;
        (endpoint.params?.path ?? []).forEach(param => {
            const val = values[param.name];

            if (val && val !== "") {
                newUrl = newUrl.replace(new RegExp(`<[^:<>]+:${param.name}>`, "g"), val);
                newUrl = newUrl
                    .replaceAll(`{${param.name}}`, val)
                    .replaceAll(`:${param.name}`, val);
            } else {
                newUrl = newUrl.replace(new RegExp(`<[^:<>]+:${param.name}>`, "g"), `{${param.name}}`);
                newUrl = newUrl
                    .replaceAll(`{${param.name}}`, `{${param.name}}`)
                    .replaceAll(`:${param.name}`, `{${param.name}}`);
            }
        });

        // Query params
        let url = restApiUrl + newUrl;
        if (endpoint.method === "GET" && endpoint.class === "list" && endpoint.params?.query?.length) {
            const searchParams = new URLSearchParams();
            endpoint.params.query.forEach(param => {
                const value = values[param.name];
                if (value !== undefined && value !== "") {
                    searchParams.append(param.name, value);
                }
            });
            const qs = searchParams.toString();
            if (qs) {
                url += (url.includes('?') ? '&' : '?') + qs;
            }
        }

        return url;
    };

    // Generate the url with its path params
    const generateUrlWithParams = (url: string, params: ApiParam[], values: Record<string, string>) => {
        let newUrl = url;
        params?.forEach(param => {
            const val = values[param.name] || '';

            // Replace the django style patterns, such as <int:job_id>, <slug:slug>, <str:name>
            newUrl = newUrl.replace(new RegExp(`<[^:<>]+:${param.name}>`, "g"), val);

            // Replace other notations, such as {job_id}, :job_id
            newUrl = newUrl
                .replaceAll(`{${param.name}}`, val)
                .replaceAll(`:${param.name}`, val);
        });
        return newUrl;
    };

    // Determine if the given data is a json string
    function isJsonString(data: any) {
        if (typeof data === "object") return true;
        if (typeof data !== "string") return false;
        try {
            const parsed = JSON.parse(data);
            return typeof parsed === "object";
        } catch {
            return false;
        }
    }

    // Handle request
    const handleRequest = async (endpoint: ApiEndpoint) => {
        setIsLoading(true);
        setConsoleData(prev => ({ ...prev, response: null }));

        try {
            // Generate the url with path and query params
            const urlWithPathParams = generateUrlWithParams(endpoint.url, endpoint.params?.path ?? [], consoleData.inputs);

            // Check for missing required path params
            const missingRequiredParams = (endpoint.params?.path ?? [])
                .filter(param => param.required && (!consoleData.inputs[param.name] || consoleData.inputs[param.name].trim() === ""))
                .map(param => param.name);

            if (missingRequiredParams.length > 0) {
                setConsoleData(prev => ({
                    ...prev,
                    response: {
                        status: 400,
                        body: `Missing required path parameter(s): ${missingRequiredParams.join(", ")}`,
                        error: "Request failed with status code 400"
                    }
                }));
                setIsLoading(false);
                return;
            }

            // Prevent accidentally sending `{}` placeholders
            if (urlWithPathParams.includes("{}") || urlWithPathParams.includes("//")) {
                setConsoleData(prev => ({
                    ...prev,
                    response: { 
                        status: 400, 
                        body: "Missing required path parameter.", 
                        error: "Request failed with status code 400"
                    }
                }));
                setIsLoading(false);
                return;
            }

            // Set the url
            let url = restApiUrl + urlWithPathParams;

            // Add query params to the url if they are set
            if (endpoint.method === "GET" && endpoint.class === "list" && endpoint.params?.query?.length) {
                const searchParams = new URLSearchParams();
                endpoint.params.query.forEach(param => {
                    const value = consoleData.inputs[param.name];
                    if (value !== undefined && value !== "") {
                        searchParams.append(param.name, value);
                    }
                });
                const qs = searchParams.toString();
                if (qs) {
                    url += (url.includes('?') ? '&' : '?') + qs;
                }
            }

            // Construct the headers
            let headers: Record<string, string> = {
                ...endpoint.request_example.default_headers,
                ...consoleData.headers
            };

            // Remove empty headers
            Object.keys(headers).forEach(k => { if (!headers[k]) delete headers[k]; });

            // Prepare data for post and patch requests
            let data;
            if (["POST", "PATCH"].includes(endpoint.method)) {

                // Parse the body to json
                try {
                    data = consoleData.body ? JSON.parse(consoleData.body) : {};

                // Show error if given body is not a json
                } catch {
                    setIsLoading(false);
                    setConsoleData(prev => ({
                        ...prev,
                        response: { status: 0, body: null, error: "Invalid JSON in body" }
                    }));
                    return;
                }
            }

            // Send the request to the backend
            const response = await axios({
                url,
                method: endpoint.method.toLowerCase(),
                headers,
                ...(data && { data }),
            });

            // Set the response
            setConsoleData(prev => ({ ...prev, response: { status: response.status, body: response.data }}));

        // Set error message
        } catch (err: any) {
            setConsoleData(prev => ({ ...prev,
                response: {
                    status: err?.response?.status || 0,
                    body: err?.response?.data || err?.toString(),
                    error: err?.message
                }
            }));
        } finally {
            setIsLoading(false);
        }
    };

    // Helper function to render the menu in either the sidemenu or mobile dropdown
    const renderMenu = () => (
        <>
            <h4>Introduction</h4>
            <ul>
                {data?.introduction.map(section => (
                    <li key={section.name}
                        className={selectedSection?.type === "intro" && selectedSection?.name === section.name ? 'active' : ''}
                        onClick={() => {
                            handleScrollToSection('intro', section.name);
                            setIsMobileMenuOpen(false);
                        }}>
                        <div className='section-button'>
                            {section.name}
                        </div>
                    </li>
                ))}
            </ul>

            <h4>Reference</h4>
            {data?.reference.map((group) => (
                <div key={group.name}
                        className='group'>
                    <div className={`group-button ${selectedSection?.type === "group" && selectedSection?.name === group.name ? 'active' : ''}`}
                            onClick={() => {
                                handleScrollToSection('group', group.name);
                                setIsMobileMenuOpen(false);
                            }}>
                        <div className='section-button'>
                            {group.name}
                        </div>
                    </div>

                    {/* Optional subgroup buttons */}
                    {openGroup === group.name && group.subgroups.length > 0 && (
                        <ul>
                            {group.subgroups.map((subgroup) => (
                                <li key={subgroup.name}
                                    className={selectedSection?.type === "subgroup" && selectedSection?.name === subgroup.name ? 'active' : ''}
                                    onClick={() => {
                                        handleScrollToSection('subgroup', subgroup.name!, group.name);
                                        setIsMobileMenuOpen(false);
                                    }}>
                                    <div className='section-button'>
                                        {subgroup.name}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            ))}
        </>
    );

    return (
        <div className={`api-docs ${activeEndpoint ? 'right-panel-opened' : ''}`}>

            {/* Mobile menu */}
            <div className="api-docs-mobile-nav-wrapper">
                <div className="api-docs-mobile-navbar">
                    <div className="logo-container">
                        <img src={logoBlack} alt="Logo" className="docs-logo" />
                    </div>
                    <button className="hamburger-button"
                            onClick={() => setIsMobileMenuOpen(prev => !prev)}>
                        <FontAwesomeIcon icon={faBars} />
                    </button>
                </div>
                {isMobileMenuOpen && (
                    <div className="api-docs-mobile-dropdown">
                        {renderMenu()}
                    </div>
                )}
            </div>

            {/* Side menu */}
            <div className='sidemenu'>
                <div className='logo-container'>
                    <img src={logoBlack} alt="Logo" className='docs-logo'/>
                </div>
                {renderMenu()}
            </div>

            {/* Main content */}
            <div className='main-content'>
                <div className="main-content__inner">

                    {/* Introduction */}
                    <h1>Crafter REST API documentation v3</h1>
                    <div className='introduction'>
                        {data?.introduction.map((section) => (
                            <div key={section.name}
                                 className='introduction__section'
                                 ref={(element) => (introRefs.current[section.name] = element)}>
                                <h3>{section.name}</h3>
                                <div className='introduction__paragraph' 
                                     dangerouslySetInnerHTML={{ __html: section.description }} 
                                />
                            </div>
                        ))}
                    </div>

                    {/* Reference */}
                    <h1>Reference Documentation</h1>
                    {data?.reference.map((group) => (
                        <div key={group.name}
                            ref={(element) => (groupRefs.current[group.name] = element)}>
                            <div className='main-content__group-header'>
                                <h2>{group.name}</h2>
                                {group.description && (
                                    <p style={{ whiteSpace: "pre-line" }}>
                                        {group.description}
                                    </p>
                                )}
                            </div>
                            {group.subgroups.length > 0 ? (
                                group.subgroups.map((subgroup) => (
                                    <div key={subgroup.name}
                                        ref={(element) => subgroup.name && (subgroupRefs.current[subgroup.name] = element)}
                                        className='subgroup'>
                                        {subgroup.name && (
                                            <h3>{subgroup.name}</h3>
                                        )}
                                        {subgroup.description && (
                                            <p style={{ whiteSpace: "pre-line" }}>
                                                {subgroup.description}
                                            </p>
                                        )}
                                        <ul>
                                            {subgroup.endpoints.map((endpoint) => (
                                                <li key={endpoint.endpoint_name}>
                                                    <button className={`endpoint-button ${activeEndpoint?.endpoint_name === endpoint.endpoint_name ? 'active' : ''}`}
                                                            onClick={() => handleToggleEndpoint(endpoint, group.name, subgroup.name)}>
                                                        <div className='left-items'>
                                                            <div className={`action-tag ${endpoint.method.toLowerCase()}`} />
                                                            <h4>
                                                                {endpoint.endpoint_name}
                                                            </h4>
                                                        </div>
                                                        <div className='icon'>
                                                            <FontAwesomeIcon icon={faAngleRight} />
                                                        </div>
                                                    </button>
                                                    <p>{endpoint.description}</p>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))
                            ) : (
                                group.endpoints.length > 0 && (
                                    <ul>
                                        {group.endpoints.map((endpoint) => (
                                            <li key={endpoint.endpoint_name}>
                                                <button className={`endpoint-button ${activeEndpoint?.endpoint_name === endpoint.endpoint_name ? 'active' : ''}`}
                                                        onClick={() => handleToggleEndpoint(endpoint, group.name)}>
                                                    <div className='left-items'>
                                                        <div className={`action-tag ${endpoint.method.toLowerCase()}`} />
                                                        <h4>
                                                            {endpoint.endpoint_name}
                                                        </h4>
                                                    </div>
                                                    <div className='icon'>
                                                        <FontAwesomeIcon icon={faAngleRight} />
                                                    </div>
                                                </button>
                                                <p>{endpoint.description}</p>
                                            </li>
                                        ))}
                                    </ul>
                                )
                            )}
                        </div>
                    ))}
                </div>
            </div>  
            {activeEndpoint && (
                <div className="right-panel">
                    <div className="right-panel__header">
                        <h3>{activeEndpoint.endpoint_name}</h3>
                        <FontAwesomeIcon
                            icon={faXmark}
                            className='right-panel__header__close-button'
                            onClick={() => {
                                setActiveEndpoint(null);
                                history.replace(location.pathname.split('/').slice(0, -1).join('/') || DOCS_BASE_PATH);
                            }}
                        />
                    </div>
                    
                    <div className='right-panel__endpoint'>
                        <span className='method'>{activeEndpoint.method}</span>{" "}
                        <span className='url'>
                            {tryMode && ((activeEndpoint.params?.path ?? []).length > 0 || (activeEndpoint.params?.query ?? []).length > 0) ? ( 
                                generatePreviewUrl(activeEndpoint, consoleData.inputs)
                            ) : (
                                <>{restApiUrl}{activeEndpoint.url}</>
                            )}                            
                        </span>
                    </div>

                    {((activeEndpoint.params?.path ?? []).length > 0 || (activeEndpoint.params?.query ?? []).length > 0) && (
                        <div className='right-panel__params'>
                            <h4>
                                {activeEndpoint.class === 'list' ? 'Query Parameters' : 'Path Parameters'}
                            </h4>                        
                            <ul>
                                {activeEndpoint.params.path?.map((parameter) => (
                                    <li key={parameter.name}>
                                        <div className={`field_name ${parameter.required ? 'required' : ''}`}>
                                            {parameter.name}
                                            {parameter.required && (
                                                <span> (required)</span>
                                            )}
                                        </div>
                                        {parameter.type}
                                    </li>
                                ))}
                                {activeEndpoint.params.query?.map((parameter) => (
                                    <li key={parameter.name}>
                                        <div className={`field_name ${parameter.required ? 'required' : ''}`}>
                                            {parameter.name}
                                            {parameter.required && (
                                                <span> (required)</span>
                                            )}
                                        </div>
                                        {parameter.type}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {activeEndpoint.properties && (
                        <div className='right-panel__properties'>

                            {/* Render main properties */}
                            {activeEndpoint.properties["main"] && (
                                <>
                                    <h4>Properties</h4>
                                    <ul>
                                        {activeEndpoint.properties["main"].map((property) => (
                                            <li key={property.name}>
                                                <div>
                                                    <div className={`field_name ${property.required ? 'required' : ''}`}>
                                                        {property.name}
                                                        {property.required && (
                                                            <span> (required)</span>
                                                        )}
                                                    </div>
                                                    {property.description}
                                                </div>
                                                <div className='field_type'>
                                                    {property.type}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            {/* Render optional related object properties */}
                            {Object.entries(activeEndpoint.properties)
                                .filter(([modelName]) => modelName !== "main")
                                .map(([modelName, properties]) => (
                                    <div key={modelName} className="right-panel__properties__section">
                                        <h4>{modelName} properties</h4>
                                        <ul>
                                            {(properties as FieldProperty[]).map((property) => (
                                                <li key={property.name}>
                                                    <div>
                                                        <div className={`field_name ${property.required ? 'required' : ''}`}>
                                                            {property.name}
                                                            {property.required && (
                                                                <span> (required)</span>
                                                            )}
                                                        </div>
                                                        {property.description}
                                                    </div>
                                                    <div className='field_type'>
                                                        {property.type}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))
                            }
                        </div>
                    )}

                    <div className='right-panel__request'>
                        <div className='right-panel__request__header'>
                            <h4>Request</h4>
                            <div className="right-panel-secondary-button" onClick={() => setTryMode(!tryMode)}>
                                {tryMode ? "Switch to Example" : "Switch to Try Mode"}
                            </div>
                        </div>

                        <div className='right-panel__request__subtitle'>Headers</div>
                        {!tryMode ? (
                            <div className='default-headers'>
                                <div><span className='label'>Content-Type:</span>{activeEndpoint.request_example?.default_headers.content_type}</div>
                                <div><span className='label'>Authorization:</span>{activeEndpoint.request_example?.default_headers.authorization}</div>
                            </div>
                        ) : (
                            <div className="right-panel__request__try_panel">
                                <div className='right-panel__request__try_panel__field'>
                                    <div className='right-panel__request__try_panel__label'>
                                        Content-Type
                                    </div>
                                    <input
                                        type="text"
                                        disabled={true}
                                        className='disabled'
                                        value={consoleData.headers['content_type'] ?? activeEndpoint.request_example.default_headers['content_type']}
                                    />
                                </div>
                                <div className='right-panel__request__try_panel__field'>
                                    <div className='right-panel__request__try_panel__label'>
                                        Authorization
                                    </div>
                                    <input
                                        type="text"
                                        value={consoleData.headers['authorization'] ?? activeEndpoint.request_example.default_headers['authorization']}
                                        onChange={e => handleConsoleChange("headers", 'authorization', e.target.value)}
                                        placeholder='Content-Type'
                                    />
                                </div>
                            </div>
                        )}

                        {tryMode && ((activeEndpoint.params?.path ?? []).length > 0 || (activeEndpoint.params?.query ?? []).length > 0) && (
                            <div style={{ marginTop: '2.5rem' }}>
                                <div className='right-panel__request__subtitle'>Params</div>
                                <div className="right-panel__request__try_panel">
                                    {activeEndpoint.params.path?.map(param => (
                                        <div key={param.name} 
                                            className="right-panel__request__try_panel__field">
                                            <div className='right-panel__request__try_panel__label'>
                                                {param.name} 
                                                {param.required ? " (required)" : ""}
                                            </div>
                                            <input
                                                type="text"
                                                value={consoleData.inputs[param.name] || ""}
                                                onChange={e => handleConsoleChange("inputs", param.name, e.target.value)}
                                                placeholder={param.type}
                                            />
                                        </div>
                                    ))}
                                    {activeEndpoint.params.query?.map(param => (
                                        <div key={param.name} 
                                            className="right-panel__request__try_panel__field">
                                            <div className='right-panel__request__try_panel__label'>
                                                {param.name} 
                                                {param.required ? " (required)" : ""}
                                            </div>
                                            <input
                                                type="text"
                                                value={consoleData.inputs[param.name] || ""}
                                                onChange={e => handleConsoleChange("inputs", param.name, e.target.value)}
                                                placeholder={param.type}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {["POST", "PATCH"].includes(activeEndpoint.method) && (
                            <div className='request-body'>
                                <div className='right-panel__request__subtitle'>Body</div>
                                {!tryMode ? (
                                    <pre>
                                        {renderJsonWithHighlight(activeEndpoint.request_example?.request_body || {})}
                                    </pre>
                                ) : (
                                    <div className='right-panel__request__try_panel'>
                                        <textarea
                                            value={consoleData.body}
                                            onChange={e => handleConsoleChange("body", e.target.value)}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {tryMode && (
                            <div className='right-panel__request__try_button'>
                                <button className="right-panel-primary-button"
                                    onClick={() => handleRequest(activeEndpoint)}
                                    disabled={isLoading}>
                                    {isLoading ? "Calling Resource..." : "Call Resource"}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Example response or request response */}
                    {!tryMode ? (
                        <div className='right-panel__response'>
                            <div className='right-panel__response__header'>
                                <h4>Response</h4>
                            </div>
                            <div className='right-panel__request__status'>
                                <pre>
                                    {activeEndpoint.response_example.status_code}
                                </pre>
                            </div>
                            {activeEndpoint.response_example.body && (
                                <pre>
                                    {renderJsonWithHighlight(activeEndpoint.response_example.body)}
                                </pre>
                            )}
                        </div>
                    ) : (consoleData.response && (
                        <div className='right-panel__response'>
                            <div className='right-panel__response__header'>
                                <h4>Response</h4>
                            </div>
                            <div className='right-panel__request__status'>
                                <pre>
                                    {consoleData.response.status}
                                    {consoleData.response.status === 204 && (
                                        ' - Object deleted successfully'
                                    )}
                                    {consoleData.response.error && (
                                        <> - {consoleData.response.error}</>
                                    )}
                                </pre>
                            </div>
                            {consoleData.response.body && (
                                isJsonString(consoleData.response.body) ? (
                                    renderJsonWithHighlight(
                                        typeof consoleData.response.body === "string"
                                            ? JSON.parse(consoleData.response.body)
                                            : consoleData.response.body
                                    )
                                ) : (
                                    <pre className='right-panel__response__try_response'>
                                        {typeof consoleData.response.body === "string"
                                            ? consoleData.response.body
                                            : JSON.stringify(consoleData.response.body, null, 2)}
                                    </pre>
                                )
                            )}
                        </div>
                    ))}
			    </div>
            )}
        </div>
    );
};

export default ApiDocs;